import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supabase } from '../../utils/supabase';
import { Equipment, EquipmentState } from '../../types/equipment';

export const fetchEquipment = createAsyncThunk(
  'equipment/fetchEquipment',
  async (_, thunkAPI) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { data, error } = await supabase
        .from('equipment')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      return data as Equipment[];
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const addEquipment = createAsyncThunk(
  'equipment/addEquipment',
  async (equipment: Omit<Equipment, 'id' | 'created_at' | 'user_id'>, thunkAPI) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { data, error } = await supabase
        .from('equipment')
        .insert([{ ...equipment, user_id: user.id }])
        .select()
        .single();

      if (error) throw error;
      return data as Equipment;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateEquipment = createAsyncThunk(
  'equipment/updateEquipment',
  async ({ id, ...updates }: Partial<Equipment> & { id: string }, thunkAPI) => {
    try {
      const { data, error } = await supabase
        .from('equipment')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Equipment;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteEquipment = createAsyncThunk(
  'equipment/deleteEquipment',
  async (id: string, thunkAPI) => {
    try {
      const { error } = await supabase
        .from('equipment')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const initialState: EquipmentState = {
  equipment: [],
  loading: false,
  error: null,
};

const equipmentSlice = createSlice({
  name: 'equipment',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEquipment.fulfilled, (state, action) => {
        state.loading = false;
        state.equipment = action.payload;
        state.error = null;
      })
      .addCase(fetchEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addEquipment.fulfilled, (state, action) => {
        state.equipment.push(action.payload);
      })
      .addCase(updateEquipment.fulfilled, (state, action) => {
        const index = state.equipment.findIndex(e => e.id === action.payload.id);
        if (index !== -1) {
          state.equipment[index] = action.payload;
        }
      })
      .addCase(deleteEquipment.fulfilled, (state, action) => {
        state.equipment = state.equipment.filter(e => e.id !== action.payload);
      });
  },
});

export default equipmentSlice.reducer;